import { t } from 'i18n';
import React, { ReactNode, useEffect, useState } from 'react';
import { Button, Card, CardProps, Container, styled, SvgIcon } from '@teko/ui';
import Link from 'next/link';
import { Box, Flex } from 'reflexbox';
import { ListTile } from '@teko/ecommerce';
import { PromoOutlined } from '@teko/ds-icon';
import { Loading } from 'components/Loading';
import { EmptyCartPanel } from 'components/EmptyCartPanel';
import { useCartV2, useTenantConfig } from 'contexts';
import { useAuth } from '@teko/auth-react';
import {
  flattenPromotionsFromPromotionGroups,
  getItemLinkObj,
  getCartTitle,
} from '../utils';
import {
  ORDER_SESSION_STORAGE_KEY,
  INLINE_BANNER_CLASSNAME,
} from '../../../constants';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import { PriceSummary } from '../shared';
import { CartLineList } from './CartLineList';
import { OrderPromotionsModal } from './OrderPromotionsModal';
import {
  Cart as CartDataInterface,
  UserAction,
  UserActionType,
} from '@teko/cart/v2';
import { BlockPromotion } from '@teko/ui-cart';
import {
  Breadcrumbs,
  Button as DesktopButton,
  ThemeProps,
  Title,
} from '@teko/ds-desktop';

import {
  getTrackingData,
  trackCustomEvent,
  trackManually,
} from '@teko/tracking';
import { ConditionalWrapper } from '@teko/utils';
import { BannerInlinePlaceholder } from '@tekface/smart-activation';

const MiniDropdown = dynamic(() =>
  import('@teko/ecommerce').then((mod) => mod.MiniDropdown),
);

const Modal = dynamic(() =>
  import('@teko/ds-desktop').then((mod) => mod.Modal),
);

const ModalBody = dynamic(() =>
  import('@teko/ds-desktop').then((mod) => mod.ModalBody),
);

const ModalHeader = dynamic(() =>
  import('@teko/ds-desktop').then((mod) => mod.ModalHeader),
);

interface StyledCardProps extends CardProps {
  firstDom?: boolean;
}

const StyledContainer = styled(Container)`
  min-height: 70vh;
  @media print {
    display: none;
  }
`;
const StyledEmptyCartPanel = styled(EmptyCartPanel)`
  width: 400px;
`;
const EmptyWrapper = styled(Card)`
  display: flex;
  align-items: center;
  justify-content: center;
`;
const StyledCard = styled(Card)<StyledCardProps>`
  padding: 1rem;
  margin-top: ${(p) => (p.firstDom ? '0' : '1rem')};
`;
const PaymentButton = styled(Button)`
  padding: 0.7rem 1rem;
  margin-top: 1rem;
`;
const PaymentLoginButton = styled(Button)`
  padding: 0.4rem 1rem;
  margin-top: 1rem;
  display: flex;
  flex-direction: column;

  .help {
    font-size: 12px;
  }
`;
const ClearCart = styled(Button)`
  &:hover {
    color: red;
  }
`;
const FlexSpaceBetween = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Print = styled.div`
  display: none;
  @media print {
    position: absolute;
    top: 0;
    background-color: white;
    display: block;
    z-index: 100001; // larger than pushdy css
    width: 100%;
    min-height: 100vh;
    overflow-y: auto;
  }
`;
const DropdownWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;
const StyledListTile = styled(ListTile)`
  padding: 0.4rem 0.6rem;
  font-size: 0.875rem;
  text-align: left;
`;

const NoOrderPromotionText = styled.div(
  ({ theme }: ThemeProps) => `
  font-size: 15px;
  line-height: 24px;
  margin-right: 12px;
  color: ${theme?.colors.textSecondary};
`,
);

const PaymentButtonWrapper = styled.div``;

/*
 * Function to add more field such as link to cart
 *
 * */
export const cookCart = (
  cart: CartDataInterface,
  epicOmni1286?: boolean,
): CartDataInterface => {
  return {
    ...cart,
    items: cart.items.map((itemGroup) => ({
      ...itemGroup,
      items: itemGroup.items.map((item) => {
        const childItems = item.childItems || [];
        return {
          ...item,
          childItems: epicOmni1286
            ? !!childItems.length
              ? childItems.map((childItem) => {
                  return {
                    ...childItem,
                    link: getItemLinkObj(childItem),
                  };
                })
              : []
            : childItems,
          link: !!childItems.length
            ? getItemLinkObj(childItems[0])
            : getItemLinkObj(item),
        };
      }),
    })),
  };
};

interface CartProps {
  cartData: CartDataInterface;
  allowExportPrice?: boolean;
  pricePDFTemplate?: ReactNode;

  // on each item's line
  itemMaximumQuantity?: number;
  onEditPrice?: (
    id: string,
    price: number,
    defaultExpectedPrice: number,
    maxConsultationServiceFee?: number,
  ) => void;
}

// for desktop to open order promotion popup after login successful
const SESSION_STORAGE_ORDER_PROMOTION_POPUP_KEY =
  'SESSION_STORAGE_ORDER_PROMOTION_POPUP';

const SESSION_STORAGE_ORDER_PROMOTION_POPUP_OPEN_STATE = 'open';

export const Cart: React.FunctionComponent<CartProps> = ({
  cartData,
  allowExportPrice = false,
  pricePDFTemplate,
  itemMaximumQuantity,
  onEditPrice,
}: CartProps) => {
  const { epicOmni1286, isNotShowWarningApplyCoupon } = useTenantConfig();
  const cart = cookCart(cartData, epicOmni1286);
  const {
    loading,
    isEmpty,
    flattenItems,
    onDeleteCart,
    onApplyPromotionInCartPage,
    onRemovePromotion,
    onApplyVoucherInCartPage,
    getCart,
  } = useCartV2();

  // all promotions of cart (not being grouped)
  const allCartPromotions = flattenPromotionsFromPromotionGroups(
    cart.promotions || [],
  );

  // filter all promotions with cartPromotionId is not null
  const appliedPromotions =
    allCartPromotions?.filter(({ cartPromotionId }) => !!cartPromotionId) || [];

  // check if user can edit price
  const isAllowEditPrice =
    cart.allowedUserActions.some(
      (action: UserAction) =>
        action.action === UserActionType.REQUEST_ON_DEMAND_DISCOUNT ||
        action.action === UserActionType.REQUEST_CONSULTATION_FEE,
    ) || false;

  const { user, authService, loading: isFetchingIAMState } = useAuth();

  const [showConfirmDeleteCartPopup, setShowConfirmDeleteCartPopup] =
    useState(false);
  const [showOrderPromotionsPopup, setShowOrderPromotionsPopup] =
    useState(false);

  const disableAction =
    isFetchingIAMState ||
    loading ||
    flattenItems
      .filter((item) => !item.isDisabled)
      .every((item) => !item.isSelected);

  useEffect(() => {
    // re-payment
    // this uses for clear previous order in sessionStorage if it exists
    sessionStorage.removeItem(ORDER_SESSION_STORAGE_KEY);

    // reload cart each time user enter cart
    getCart({ withLoading: false });

    // put getCart here will pose potential rendering risks since getCart is not a React state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // check to open order promotion popup if user login successful, and returned from login page
  useEffect(() => {
    const orderPromotionPopupState = sessionStorage.getItem(
      SESSION_STORAGE_ORDER_PROMOTION_POPUP_KEY,
    );
    if (
      orderPromotionPopupState ===
        SESSION_STORAGE_ORDER_PROMOTION_POPUP_OPEN_STATE &&
      !!user
    ) {
      setShowOrderPromotionsPopup(true);
      sessionStorage.removeItem(SESSION_STORAGE_ORDER_PROMOTION_POPUP_KEY);
    }
  }, [user]);

  // user has to login before enter code/apply order promotion
  const handleClickOrderPromotions = () => {
    // have to user isLoggedIn() method to defend case user has logged out in other tab
    trackManually(
      'clickOrderPromotions',
      'cartRightSidebar',
      'orderPromotionPopup',
    );
    if (!authService.isLoggedIn()) {
      // save a temporary open state of popup to open it later
      sessionStorage.setItem(
        SESSION_STORAGE_ORDER_PROMOTION_POPUP_KEY,
        SESSION_STORAGE_ORDER_PROMOTION_POPUP_OPEN_STATE,
      );
      authService.login(`${window.location.origin}/cart`);
      return;
    }
    trackCustomEvent('showOrderPromotionPopup', {});
    setShowOrderPromotionsPopup(true);
  };

  const getCartContent = (): React.ReactNode => {
    if (!isFetchingIAMState && !loading && isEmpty) {
      return (
        <EmptyWrapper>
          <StyledEmptyCartPanel />
        </EmptyWrapper>
      );
    }
    if (!!cart.items.length) {
      return (
        <>
          <Head>
            <meta name="robots" content="noindex, nofollow" />
          </Head>
          <Flex marginBottom={16}>
            <Box width="67%" marginRight={16}>
              <FlexSpaceBetween>
                <Title className="att-gio-hang" level={1}>
                  {getCartTitle(
                    cart?.orderId || '',
                    cart?.isClone || false,
                    flattenItems.length,
                  )}
                </Title>
                <ClearCart
                  link
                  variant="secondary"
                  onClick={(): void => setShowConfirmDeleteCartPopup(true)}
                  className="att-delete-all-btn"
                >
                  {t('Xóa tất cả')}
                </ClearCart>
              </FlexSpaceBetween>
            </Box>
            {allowExportPrice && (
              <Box width="33%">
                <DropdownWrapper>
                  <MiniDropdown text={t('Tải báo giá')}>
                    <StyledListTile
                      leading={<SvgIcon name="pdf" size={16} />}
                      title="PDF"
                      onClick={(): void => window.print()}
                    />
                  </MiniDropdown>
                </DropdownWrapper>
              </Box>
            )}
          </Flex>
          <Flex>
            <Box width="67%" marginRight={16}>
              <CartLineList
                items={cart.items || []}
                onEditPrice={onEditPrice}
                isAllowEditPrice={isAllowEditPrice}
                itemMaximumQuantity={itemMaximumQuantity}
              />
            </Box>
            <Box width="33%">
              <OrderPromotionsModal
                promotionGroups={cart.promotions || []}
                onApplyVoucher={onApplyVoucherInCartPage}
                onApplyPromotion={onApplyPromotionInCartPage}
                onRemovePromotion={onRemovePromotion}
                visible={showOrderPromotionsPopup}
                onClose={() => setShowOrderPromotionsPopup(false)}
              />
              <StyledCard
                firstDom
                marginTop="0"
                header={
                  <FlexSpaceBetween>
                    <h6 style={{ marginBottom: 0 }}>{t('Khuyến mãi')}</h6>
                    <DesktopButton
                      id="select-coupon"
                      variant="link"
                      onClick={handleClickOrderPromotions}
                    >
                      <PromoOutlined /> &nbsp; {t('Chọn hoặc nhập khuyến mãi')}
                    </DesktopButton>
                  </FlexSpaceBetween>
                }
              >
                {!allCartPromotions.length && (
                  <NoOrderPromotionText>
                    {t(
                      'Đơn hàng chưa đủ điều kiện áp dụng khuyến mãi. Vui lòng mua thêm để áp dụng',
                    )}
                  </NoOrderPromotionText>
                )}
                {!!appliedPromotions.length && (
                  <BlockPromotion
                    promotions={appliedPromotions}
                    title={t('Khuyến mãi đã áp dụng').toUpperCase()}
                    onRemovePromotion={onRemovePromotion}
                    backgroundColor="white"
                    warningText={
                      !isNotShowWarningApplyCoupon
                        ? t(
                            'Mã giảm giá/Phiếu mua hàng sẽ không thể khôi phục sau khi đặt hàng',
                          )
                        : undefined
                    }
                    styledPromotionItem={{ marginTop: 's8' }}
                  />
                )}
              </StyledCard>
              <StyledCard
                firstDom={false}
                header={<h6>{t('Thanh toán')}</h6>}
                footer={
                  <>
                    {user ? (
                      <ConditionalWrapper
                        wrapper={Link}
                        condition={!disableAction}
                        href="/checkout"
                        legacyBehavior
                      >
                        <PaymentButtonWrapper
                          {...getTrackingData({
                            isTrackContent: true,
                            regionName: 'bottomShoppingCart',
                            contentName: 'createOrder',
                          })}
                        >
                          <PaymentButton
                            variant="primary"
                            block
                            disabled={disableAction}
                            className="att-shopping-btn"
                          >
                            {t('TIẾP TỤC')}
                          </PaymentButton>
                        </PaymentButtonWrapper>
                      </ConditionalWrapper>
                    ) : (
                      <PaymentLoginButton
                        variant="primary"
                        block
                        onClick={(): void => {
                          authService.login(
                            `${window.location.origin}/checkout`,
                          );
                        }}
                      >
                        {t('THANH TOÁN')}
                        <span className="help">
                          {t('Bạn cần đăng nhập để tiếp tục')}
                        </span>
                      </PaymentLoginButton>
                    )}
                  </>
                }
              >
                {!!cart.priceSummary.length && (
                  <PriceSummary
                    priceSummary={cart.priceSummary}
                    total={cart.grandTotalAmount}
                  />
                )}
              </StyledCard>
              <BannerInlinePlaceholder
                className={INLINE_BANNER_CLASSNAME.INLINE_CART}
              />
              <div className="cartInlineBox" />
            </Box>
          </Flex>
        </>
      );
    }
  };
  return (
    <Loading isLoading={isFetchingIAMState || loading}>
      {!isEmpty && !!cart?.items.length && !!allowExportPrice && (
        <Print>{pricePDFTemplate}</Print>
      )}
      <StyledContainer marginTop={16} marginBottom={16}>
        <Breadcrumbs
          breadcrumbs={[
            {
              link: '/',
              name: t('Trang chủ'),
            },
            {
              disabled: true,
              link: '#',
              name: t('Giỏ hàng'),
            },
          ]}
        />
        {getCartContent()}
      </StyledContainer>
      {/* Popup for confirming delete cart */}
      <Modal
        visible={showConfirmDeleteCartPopup}
        onClose={(): void => setShowConfirmDeleteCartPopup(false)}
        width="400px"
        okText="Đồng ý"
        onOk={(): Promise<void> =>
          onDeleteCart().then(() => setShowConfirmDeleteCartPopup(false))
        }
        cancelText="Hủy bỏ"
        onCancel={(): void => setShowConfirmDeleteCartPopup(false)}
        alignFooter="center"
        okButtonProps={{
          className: 'att-approved-remove-all-cart-items',
        }}
      >
        <ModalHeader>{t('Chú ý')}</ModalHeader>
        <ModalBody>
          {t('sites.shop-front.components.desktop.pages.Cart.1')}
        </ModalBody>
      </Modal>
    </Loading>
  );
};
export const TRACKING_PAGE_VIEWS = {
  screenName: 'shoppingCart',
  contentType: 'checkout',
};
